import React, { useEffect } from "react";
import { useRouter } from "next/router";
import Head from "next/head";
import getConfig from "next/config";
import { connect } from "react-redux";
import { resizeImage } from "@lib/helpers";
import Breadcrumbs from "@components/Breadcrumbs";
import ContentEditorView from "@components/ContentEditorView/index";
import ProductListResealed from "@components/ProductList/ProductListResealed";
import ResealedFilters from "@components/Filters/ResealedFilters";
import ActiveFilters from "@components/Filters/ActiveFilters";
import ResealedSort from "@components/ResealedSort";
import { trackProductClick } from "@store/tracker/actions";
import CompareWidget from "@containers/Catalog/CompareWidget";
import Banners from "@components/Banners";
import Heading from "@components/Heading";
import PaginationWithLinks from "@components/PaginationWithLinks";
import { getCategoryResealed } from "@store/resealed/actions";
import { setGTMProductsImpressions } from "@lib/GTMHelper";

const {
  publicRuntimeConfig: {
    settings: { websiteStatics, baseUrl, websiteName },
  },
} = getConfig();

const isBricoStore = websiteName === "bricostore";

const findParentCategoryById = (categoryId, categories) => {
  for (const category of categories) {
    if (category.id === categoryId) {
      return category;
    }

    if (category.subcategories?.length > 0) {
      const result = findParentCategoryById(categoryId, category.subcategories);

      if (result) {
        return result;
      }
    }
  }

  return null;
};

const getParentCategorySubcategories = (categories) => {
  const subcategories = categories
    // TODO: This is an API problem
    // Category tree returns categories without products
    // Worst case scenario, this should be moved to the API transformer
    // TODO: Verify if backend returns categories with resealed_count 0
    // and remove code if it's fixed
    .filter((category) => category.resealed_count)
    .sort((a, b) => a.id - b.id);

  return subcategories.length ? subcategories : null;
};

const CatalogResealedList = ({
  query,
  currentCategory,
  trackProductClick,
  extractedFiltersParams,
  resealedTree,
}) => {
  const { asPath } = useRouter();

  const {
    breadcrumbs = [],
    products,
    filters,
    activeFilters,
    meta,
    toolbar,
  } = currentCategory;

  const {
    meta_title = "",
    meta_description = "",
    meta_keywords = "",
    meta_robots,
    seo_canonical,
    seo_alternate_canonical = "",
    seo_title,
    name,
    description = null,
    description_editor_object = null,
    landing_page,
    banners,
    url_key,
    subcategories,
    parent_id,
    thumbnail,
  } = currentCategory.category;

  const hasSubcategories = Boolean(subcategories?.length);

  const parentCategory =
    typeof parent_id === "number" &&
    findParentCategoryById(parent_id, resealedTree);

  const parentSubcategories =
    Boolean(parentCategory?.subcategories?.length) &&
    getParentCategorySubcategories(parentCategory.subcategories);

  const hasParentSubcategories = Boolean(parentSubcategories?.length);

  const navigationCategories = hasSubcategories
    ? // TODO: Backend should sort subcategories
      // Remove when backend does that
      subcategories.sort((a, b) => a.id - b.id)
    : hasParentSubcategories && parentSubcategories;

  const navigationCategoriesWithActiveState =
    navigationCategories &&
    navigationCategories?.map((category) => ({
      ...category,
      isActive: category.url_key === url_key,
    }));

  const totalProducts = meta?.items || 0;

  let parsedDescriptionObject;

  if (description_editor_object) {
    try {
      parsedDescriptionObject = JSON.parse(description_editor_object);
    } catch (error) {
      parsedDescriptionObject = false;
    }
  }

  useEffect(() => {
    if (products?.length > 0) {
      setGTMProductsImpressions(
        products.map((product, index) => ({
          ...product,
          index: index + 1,
          categoryPath: breadcrumbs,
          item_list_name: "Produse resigilate",
        }))
      );
    }
  }, [products, breadcrumbs]);

  const Meta = () => (
    <Head>
      <title>{meta_title || name}</title>

      {meta_description && (
        <meta name="description" content={meta_description} />
      )}

      {meta_keywords.length > 0 && (
        <meta name="keywords" content={meta_keywords} />
      )}

      {thumbnail && (
        <meta property="og:image" content={resizeImage(thumbnail, "600x600")} />
      )}

      {meta_robots?.length > 0 && <meta name="robots" content={meta_robots} />}

      {seo_canonical && !seo_alternate_canonical?.length > 0 && (
        <link rel="canonical" href={`${baseUrl}${asPath}`} />
      )}

      {seo_canonical && seo_alternate_canonical?.length > 0 && (
        <link rel="alternate" href={seo_alternate_canonical} />
      )}
    </Head>
  );

  const ListLayout = () => (
    <>
      <div className="lg:flex">
        <ResealedFilters
          activeFilters={activeFilters}
          filters={filters}
          extractedFiltersParams={extractedFiltersParams}
          query={query}
          totalProducts={totalProducts}
          navigationCategories={navigationCategoriesWithActiveState}
          name={name}
        />

        <div className={`${products.length > 0 ? "lg:w-4/5" : "lg:mt-8"} `}>
          <div className="block md:flex md:items-baseline">
            <Heading
              as="h1"
              title={seo_title || name}
              display="h1"
              sameSizeOnMobile
            />

            {totalProducts > 0 && (
              <div
                className={`md:ml-2 font-normal text-22px leading-7 ${
                  isBricoStore ? "text-[#9F9FA9]" : "text-gray-light"
                }`}
              >
                ({totalProducts} {totalProducts === 1 ? "produs" : "produse"})
              </div>
            )}
          </div>

          {banners?.[0]?.images_mobile?.length > 0 && (
            <div className="sm:hidden">
              <Banners items={banners[0].images_mobile} />
            </div>
          )}

          {banners?.[0]?.images_tablet?.length > 0 && (
            <div className="hidden sm:block lg:hidden">
              <Banners items={banners[0].images_tablet} />
            </div>
          )}

          {banners?.[0]?.images?.length > 0 && (
            <div className="hidden lg:block">
              <Banners items={banners[0].images} />
            </div>
          )}

          {landing_page?.content && (
            <div dangerouslySetInnerHTML={{ __html: landing_page.content }} />
          )}

          <div>
            <ActiveFilters activeFilters={activeFilters} />

            <ResealedSort
              toolbar={toolbar}
              navigationCategories={navigationCategoriesWithActiveState}
            />

            <ProductListResealed
              products={products.map((product, index) => ({
                ...product,
                index: index + 1,
                categoryPath: breadcrumbs,
                item_list_name: "Produse resigilate",
              }))}
              filter={extractedFiltersParams}
              onProductClick={trackProductClick}
            />

            {toolbar?.paginationBottom?.length > 1 && (
              <div className="mb-6">
                <div className="flex justify-center">
                  <PaginationWithLinks pagination={toolbar.paginationBottom} />
                </div>

                <div className="text-center mt-1">
                  <Heading
                    as="h3"
                    display="h6"
                    title={`${meta.items} ${
                      meta.items === 1 ? "produs" : "produse"
                    }`}
                  />
                </div>
              </div>
            )}

            <ResealedSort
              toolbar={toolbar}
              navigationCategories={navigationCategoriesWithActiveState}
              isModal={true}
            />
          </div>

          {/* SEO page descriptions
          - should only appear on list pages without filters or sorting applied
          */}
          {Object.keys(extractedFiltersParams.filters).length === 0 &&
            Object.keys(extractedFiltersParams.otherParams).length === 0 &&
            (parsedDescriptionObject ? (
              <ContentEditorView content={parsedDescriptionObject} />
            ) : (
              description?.length > 0 && (
                <div className="my-10">{description}</div>
              )
            ))}
        </div>
      </div>
    </>
  );

  return (
    <>
      <Meta />
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      {products?.length > 0 ? (
        <ListLayout />
      ) : (
        <div className="flex flex-col md:flex-row items-center justify-center md:justify-start gap-6 my-4">
          <img
            className="w-[158px]"
            src={`${websiteStatics}/images/cart/empty-cart.svg`}
            alt=""
          />

          <Heading
            title={
              seo_title || (
                <span>
                  Nu au fost gasite produse conform criteriilor selectate.
                </span>
              )
            }
            as="h1"
            display="h6"
            classesProp="max-w-[639px]"
          />
        </div>
      )}

      <CompareWidget />
    </>
  );
};

export default connect(
  (state) => ({
    currentCategory: state.resealed.currentCategory,
    extractedFiltersParams: state.resealed.extractedFiltersParams,
    resealedTree: state.resealed.tree,
  }),
  {
    trackProductClick,
    getCategoryResealed,
  }
)(CatalogResealedList);
